<template>
  <HeaderComponent />
  <div class="fixed top-[80px] left-[50%] z-[100] translate-x-[-50%] w-[80%] max-w-[400px] flex flex-col">
    <div v-if="errorMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-red-700 my-[10px] font-bold text-center p-[20px]">{{ errorMessage }}</div>
    <div v-if="successMessage" class="text-sm w-full rounded-[5px] shadow-4xl text-white bg-green-700 my-[10px] font-bold text-center p-[20px]">{{ successMessage }}</div>
  </div>

  <section class="w-full min-h-screen pt-[100px] pb-[60px] bg-gray-100 flex justify-center">
    <div class="container lg:max-w-[800px] px-[20px] w-full">
      <router-link to="/mon-espace" class="text-sm text-gray-500 hover:text-orange-600 mb-[15px] inline-block"><i class="fa-solid fa-arrow-left mr-[5px]"></i> Retour à mon espace</router-link>

      <div v-if="isLoading" class="w-full h-[200px] flex justify-center items-center">
        <div class="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>

      <template v-else-if="plan">
        <!-- Résumé du plan -->
        <div class="w-full bg-white rounded-[10px] shadow-4xl p-[20px] mb-[20px] flex gap-[15px]">
          <div class="w-[70px] h-[70px] min-w-[70px] bg-gray-100 rounded-[8px] overflow-hidden flex items-center justify-center">
            <img v-if="plan.produit?.photo" :src="plan.produit.photo" class="w-full h-full object-cover" alt="">
            <i v-else class="fa-solid fa-mobile-screen-button text-2xl text-gray-300"></i>
          </div>
          <div class="flex-1">
            <h1 class="font-bold text-gray-800">{{ plan.produit?.nom }}</h1>
            <p class="text-xs text-gray-500">{{ plan.produit?.categorie }}</p>
            <div class="flex gap-[15px] mt-[8px] text-sm">
              <span class="text-gray-700 font-bold">{{ formatPrice(plan.prixTotal) }}</span>
              <span class="text-green-600">Payé : {{ formatPrice(plan.montantPaye) }}</span>
              <span class="text-orange-600 font-semibold">Reste : {{ formatPrice(reste) }}</span>
            </div>
          </div>
        </div>

        <!-- Formulaire de paiement -->
        <div class="w-full bg-white rounded-[10px] shadow-4xl p-[20px] mb-[20px]">
          <h2 class="font-bold text-gray-800 mb-[15px]">Payer une tranche</h2>

          <label class="text-sm font-bold text-gray-700 mb-[5px] block">Montant à créditer sur votre plan (FCFA)</label>
          <input v-model.number="montant" type="number" min="1" :max="reste" class="w-full p-[12px] outline-none text-sm font-semibold rounded-[5px] bg-gray-100 focus:ring-2 focus:ring-orange-500" placeholder="Ex: 25000">

          <!-- Détail des frais de retrait -->
          <div v-if="montant > 0" class="mt-[12px] bg-orange-50 rounded-[8px] p-[12px] text-sm">
            <div class="flex justify-between text-gray-600"><span>Montant crédité sur votre plan</span><span class="font-semibold">{{ formatPrice(montant) }}</span></div>
            <div class="flex justify-between text-gray-600 mt-[4px]"><span>Frais MoMo (0,5%)</span><span class="font-semibold">{{ formatPrice(frais) }}</span></div>
            <div class="flex justify-between items-center mt-[8px] pt-[8px] border-t border-orange-200"><span class="font-bold text-gray-800">Total à envoyer via MoMo</span><span class="font-bold text-orange-600">{{ formatPrice(montantAEnvoyer) }}</span></div>
          </div>

          <!-- Instructions MoMo -->
          <div v-if="montant > 0" class="mt-[20px] border-t border-gray-100 pt-[20px]">
            <h3 class="text-sm font-bold text-gray-800 mb-[10px]"><i class="fa-solid fa-mobile-screen text-orange-500 mr-[5px]"></i> Payer via MTN MoMo</h3>

            <p v-if="momo.instructions" class="text-xs text-gray-500 whitespace-pre-line mb-[12px]">{{ momo.instructions }}</p>
            <img v-if="momo.image" :src="momo.image" class="w-full max-h-[200px] object-contain rounded-[8px] mb-[12px]" alt="Instructions de paiement">

            <div class="flex flex-col sm:flex-row gap-[15px] items-center">
              <!-- Code USSD -->
              <div class="flex-1 w-full">
                <p class="text-xs text-gray-500 mb-[5px]">Composez ce code :</p>
                <div class="flex items-center gap-[8px] bg-gray-100 rounded-[8px] p-[12px]">
                  <span class="flex-1 font-mono font-bold text-gray-800 break-all">{{ ussd }}</span>
                  <button @click="copyUssd" class="px-[10px] py-[6px] rounded-[5px] bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 whitespace-nowrap">
                    <i class="fa-solid fa-copy mr-[3px]"></i> {{ copied ? 'Copié' : 'Copier' }}
                  </button>
                </div>
                <a :href="telLink" class="mt-[10px] w-full flex justify-center items-center py-[10px] rounded-[5px] bg-gray-900 text-white text-sm font-bold hover:bg-gray-800">
                  <i class="fa-solid fa-phone mr-[8px]"></i> Composer le code
                </a>
              </div>
              <!-- QR -->
              <div v-if="qrDataUrl" class="flex flex-col items-center">
                <img :src="qrDataUrl" class="w-[130px] h-[130px]" alt="QR Code de paiement">
                <p class="text-[10px] text-gray-400 mt-[3px]">Scannez pour composer</p>
              </div>
            </div>

            <!-- Preuve -->
            <div class="mt-[20px] border-t border-gray-100 pt-[20px]">
              <h3 class="text-sm font-bold text-gray-800 mb-[10px]">Joindre la preuve de paiement</h3>
              <input @change="onPreuveChange" type="file" accept="image/*" class="w-full text-sm">
              <img v-if="preuvePreview" :src="preuvePreview" class="mt-[10px] max-h-[180px] rounded-[8px] object-contain" alt="Aperçu">
              <button @click="submitVersement" :disabled="isSubmitting" class="w-full h-[48px] mt-[15px] flex justify-center items-center text-sm font-bold rounded-[5px] bg-orange-600 hover:bg-orange-700 text-white transition-colors">
                <span v-if="!isSubmitting">Soumettre le paiement</span>
                <div v-else class="spinner1"><div></div><div></div><div></div><div></div><div></div></div>
              </button>
            </div>
          </div>
        </div>

        <!-- Historique -->
        <div v-if="versements.length" class="w-full bg-white rounded-[10px] shadow-4xl p-[20px]">
          <h2 class="font-bold text-gray-800 mb-[15px]">Historique de mes paiements</h2>
          <div v-for="v in versements" :key="v._id" class="flex justify-between items-center py-[10px] border-b border-gray-50">
            <div>
              <p class="text-sm font-bold text-gray-800">{{ formatPrice(v.montant) }} <span class="text-xs text-gray-400 font-normal">crédité</span></p>
              <p v-if="v.montantEnvoye" class="text-xs text-gray-400">Envoyé : {{ formatPrice(v.montantEnvoye) }} (dont {{ formatPrice(v.frais) }} de frais)</p>
              <p class="text-xs text-gray-400">{{ formatDate(v.createdAt) }}</p>
              <p v-if="v.statut === 'rejete' && v.motifRejet" class="text-xs text-red-500 mt-[2px]">{{ v.motifRejet }}</p>
            </div>
            <span :class="versClass(v.statut)" class="text-[11px] px-2 py-1 rounded-full font-bold">{{ versLabel(v.statut) }}</span>
          </div>
        </div>
      </template>
    </div>
  </section>
  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import QRCode from 'qrcode'

const router = useRouter()
const planId = router.currentRoute.value.params.id

const plan = ref(null)
const momo = ref({ ussdTemplate: '*880*41*{montant}#', instructions: '', image: '' })
const versements = ref([])
const isLoading = ref(true)
const isSubmitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const montant = ref(null)
const preuveFile = ref(null)
const preuvePreview = ref('')
const qrDataUrl = ref('')
const copied = ref(false)

const formatPrice = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0)) + ' FCFA'
const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const reste = computed(() => plan.value ? Math.max((plan.value.prixTotal || 0) - (plan.value.montantPaye || 0), 0) : 0)

// Frais de retrait MoMo (0,5%), automatiquement ajoutés en plus du montant que le client veut
// voir crédité sur son plan : c'est ce total (montant + frais) qu'il doit envoyer via MoMo.
const FRAIS_RETRAIT_RATE = 0.005
const frais = computed(() => montant.value > 0 ? Math.round(montant.value * FRAIS_RETRAIT_RATE) : 0)
const montantAEnvoyer = computed(() => (montant.value || 0) + frais.value)

const ussd = computed(() => {
  const t = momo.value.ussdTemplate || '*880*41*{montant}#'
  const m = montantAEnvoyer.value
  return t.includes('{montant}') ? t.replace('{montant}', m) : t + m
})
const telLink = computed(() => 'tel:' + encodeURIComponent(ussd.value))

function versLabel(s) { return { en_attente: 'En attente', valide: 'Validé', rejete: 'Rejeté' }[s] || s }
function versClass(s) {
  if (s === 'valide') return 'bg-green-100 text-green-700'
  if (s === 'rejete') return 'bg-red-100 text-red-700'
  return 'bg-yellow-100 text-yellow-700'
}
const showMsg = (r, m) => { r.value = m; setTimeout(() => (r.value = ''), 4000) }

watch([ussd, montant], async () => {
  if (montant.value > 0) {
    try { qrDataUrl.value = await QRCode.toDataURL(telLink.value, { margin: 1, width: 260 }) } catch (e) { qrDataUrl.value = '' }
  } else {
    qrDataUrl.value = ''
  }
})

const copyUssd = async () => {
  try {
    await navigator.clipboard.writeText(ussd.value)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
  } catch (e) { /* clipboard indisponible */ }
}

const onPreuveChange = (e) => {
  preuveFile.value = e.target.files[0]
  preuvePreview.value = preuveFile.value ? URL.createObjectURL(preuveFile.value) : ''
}

const fetchAll = async () => {
  isLoading.value = true
  try {
    const [p, m, v] = await Promise.all([
      axios.get(`/api/plans/${planId}`),
      axios.get('/api/momo-config'),
      axios.get(`/api/plans/${planId}/versements`),
    ])
    plan.value = p.data
    momo.value = m.data || momo.value
    versements.value = v.data || []
    montant.value = reste.value || null
  } catch (e) {
    showMsg(errorMessage, 'Erreur lors du chargement.')
  } finally {
    isLoading.value = false
  }
}

const submitVersement = async () => {
  if (!montant.value || montant.value <= 0) { showMsg(errorMessage, 'Indiquez un montant valide.'); return }
  if (!preuveFile.value) { showMsg(errorMessage, 'Veuillez joindre la preuve de paiement.'); return }
  isSubmitting.value = true
  try {
    const fd = new FormData()
    fd.append('montant', montant.value)
    fd.append('preuve', preuveFile.value)
    await axios.post(`/api/plans/${planId}/versements`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    showMsg(successMessage, 'Paiement soumis ! En attente de validation.')
    preuveFile.value = null
    preuvePreview.value = ''
    setTimeout(() => router.push('/mon-espace'), 1200)
  } catch (e) {
    showMsg(errorMessage, e.response?.data?.error || 'Erreur lors de la soumission.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(fetchAll)
</script>
